import { Plus, Trash2, Edit2, Check } from 'lucide-react';
import { colors } from '../../styles/colors';
import { formatEuro } from '../../utils/formatters';

/**
 * FactureDetailTable - Tableau réutilisable pour les lignes de facture
 *
 * Utilisé pour les lignes de temps et les ajustements manuels.
 *
 * @param {Object} props
 * @param {string} props.type - 'temps' | 'ajustement'
 * @param {Array} props.items - Les lignes à afficher
 * @param {Array} props.allDetails - Tous les détails (pour trouver l'index réel)
 * @param {number|null} props.editingIndex - Index de la ligne en cours d'édition
 * @param {boolean} props.readOnly - Mode lecture seule
 * @param {Function} props.onEdit - Handler pour éditer une ligne
 * @param {Function} props.onSave - Handler pour sauvegarder une ligne
 * @param {Function} props.onDelete - Handler pour supprimer une ligne
 * @param {Function} props.onFieldChange - Handler pour modifier un champ
 * @param {Function} props.onAdd - Handler pour ajouter une ligne
 */
export default function FactureDetailTable({
  type,
  items,
  allDetails,
  editingIndex,
  readOnly,
  onEdit,
  onSave,
  onDelete,
  onFieldChange,
  onAdd
}) {
  const isTemps = type === 'temps';
  const headerColor = isTemps ? colors.secondary.lightTeal : colors.primary.gold;
  const title = isTemps ? 'Lignes de temps' : 'Ajustements manuels';
  const emptyMessage = isTemps ? 'Aucune ligne de temps' : 'Aucun ajustement manuel';
  const addButtonText = isTemps ? 'Ajouter une ligne de temps' : 'Ajouter un ajustement manuel';
  const addButtonColor = isTemps ? colors.secondary.lightBlue : colors.primary.gold;
  const addButtonHoverColor = isTemps ? '#2563eb' : '#d97706';

  // Colonnes pour le type temps
  const tempsColumns = [
    { key: 'numero_ligne', label: '#', width: '50px', align: 'left' },
    { key: 'consultant', label: 'Consultant', minWidth: '150px', align: 'left', editable: true },
    { key: 'client_final', label: 'Client final', minWidth: '150px', align: 'left', editable: true },
    { key: 'nombre_jours', label: 'Jours', width: '100px', align: 'left', editable: true, type: 'number', step: '0.5' },
    { key: 'description', label: 'Description', minWidth: '200px', align: 'left', editable: true },
    { key: 'montant', label: 'Montant (€)', width: '120px', align: 'right', editable: true, type: 'number', step: '0.01', format: formatEuro }
  ];

  // Colonnes pour le type ajustement
  const ajustementColumns = [
    { key: 'numero_ligne', label: '#', width: '50px', align: 'left' },
    { key: 'description', label: 'Description', align: 'left', editable: true, placeholder: "Description de l'ajustement" },
    { key: 'montant', label: 'Montant (€)', width: '150px', align: 'right', editable: true, type: 'number', step: '0.01', format: formatEuro }
  ];

  const columns = isTemps ? tempsColumns : ajustementColumns;

  const thStyle = {
    padding: '0.75rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.text.white
  };

  const tdStyle = {
    padding: '0.75rem',
    fontSize: '0.875rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.375rem',
    border: `1px solid ${colors.secondary.lightGray}`,
    borderRadius: '0.25rem',
    fontSize: '0.875rem'
  };

  const actionButtonStyle = {
    padding: '0.375rem',
    color: colors.text.white,
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  };

  return (
    <>
      <h3 style={{
        fontSize: '1.125rem',
        fontWeight: '600',
        color: colors.primary.gray,
        marginBottom: '1rem',
        marginTop: 0
      }}>
        {title}
      </h3>

      <div style={{
        border: `1px solid ${colors.secondary.lightGray}`,
        borderRadius: '0.5rem',
        overflow: 'hidden',
        marginBottom: '1rem'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: headerColor }}>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{
                    ...thStyle,
                    textAlign: col.align,
                    width: col.width,
                    minWidth: col.minWidth
                  }}
                >
                  {col.label}
                </th>
              ))}
              {!readOnly && (
                <th style={{ ...thStyle, textAlign: 'center', width: '100px' }}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (readOnly ? 0 : 1)}
                  style={{ padding: '2rem', textAlign: 'center', color: colors.secondary.mediumGray }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const actualIndex = allDetails.indexOf(item);
                const isEditing = editingIndex === actualIndex;

                return (
                  <tr
                    key={actualIndex}
                    style={{
                      borderBottom: `1px solid ${colors.secondary.lightGray}`,
                      backgroundColor: actualIndex % 2 === 0 ? colors.text.white : '#f9fafb'
                    }}
                  >
                    {columns.map(col => (
                      <td
                        key={col.key}
                        style={{
                          ...tdStyle,
                          textAlign: col.align,
                          fontWeight: col.key === 'montant' ? '600' : 'normal'
                        }}
                      >
                        {isEditing && col.editable ? (
                          <input
                            type={col.type || 'text'}
                            step={col.step}
                            value={item[col.key]}
                            onChange={(e) => onFieldChange(actualIndex, col.key, e.target.value)}
                            placeholder={col.placeholder}
                            style={{
                              ...inputStyle,
                              textAlign: col.align
                            }}
                          />
                        ) : (
                          col.format ? col.format(item[col.key]) : item[col.key]
                        )}
                      </td>
                    ))}
                    {!readOnly && (
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          {isEditing ? (
                            <button
                              onClick={onSave}
                              style={{ ...actionButtonStyle, backgroundColor: colors.primary.teal }}
                              title="Valider"
                            >
                              <Check size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => onEdit(actualIndex)}
                              style={{ ...actionButtonStyle, backgroundColor: colors.secondary.lightBlue }}
                              title="Modifier"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => onDelete(actualIndex)}
                            style={{ ...actionButtonStyle, backgroundColor: '#dc2626' }}
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <button
          onClick={onAdd}
          style={{
            marginBottom: '2rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: addButtonColor,
            color: colors.text.white,
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 200ms'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = addButtonHoverColor}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = addButtonColor}
        >
          <Plus size={18} />
          {addButtonText}
        </button>
      )}
    </>
  );
}
